import { useEffect, useState } from 'react';
import { MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import TruckLocationPicker from './TruckLocationPicker';
import TruckLocationMap from './TruckLocationMap';
import { apiFetch } from '../lib/apiClient';
import { hasExactLocation } from '../lib/truckLocation';

function locationFromTruck(truck) {
  const loc = truck?.exact_location;
  return hasExactLocation(loc) ? { lat: loc.lat, lng: loc.lng, note: loc.note || '' } : null;
}

export default function TruckLocationSettings({ truck, readOnly = false, onSaved }) {
  const [value, setValue] = useState(() => locationFromTruck(truck));
  const [saving, setSaving] = useState(false);
  const saved = truck?.exact_location;

  useEffect(() => {
    setValue(locationFromTruck(truck));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [truck?.truck_id, saved?.lat, saved?.lng, saved?.note]);

  const handleSave = async () => {
    if (!truck || readOnly) return;
    setSaving(true);
    try {
      const body = hasExactLocation(value)
        ? { lat: Number(value.lat), lng: Number(value.lng), note: value.note || null }
        : { clear: true };
      const res = await apiFetch(`/api/trucks/${truck.truck_id}/location`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(typeof data.detail === 'string' ? data.detail : 'Failed to save truck location');
      }
      onSaved?.(data);
      toast.success(body.clear ? 'Location pin removed' : 'Truck location saved');
    } catch (err) {
      toast.error(err.message || 'Failed to save truck location');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-card rounded-xl border p-4 md:p-6 space-y-4" data-testid="truck-location-settings">
      <div>
        <h3 className="font-semibold text-lg flex items-center gap-2">
          <MapPin className="w-5 h-5 text-primary" />
          Truck Location
        </h3>
        <p className="text-sm text-muted-foreground">
          Pin exactly where you're parked so students can find you. Update it whenever you move.
        </p>
        {truck?.location && (
          <p className="text-xs text-muted-foreground mt-1">Nearby building: {truck.location}</p>
        )}
      </div>

      {readOnly ? (
        hasExactLocation(saved) ? (
          <>
            {saved.note && <p className="text-sm">{saved.note}</p>}
            <TruckLocationMap location={saved} />
          </>
        ) : (
          <p className="text-sm text-muted-foreground">No exact location pinned.</p>
        )
      ) : (
        <>
          <TruckLocationPicker value={value} onChange={setValue} />
          <Button onClick={handleSave} disabled={saving} data-testid="save-truck-location-btn">
            {saving ? 'Saving…' : 'Save Location'}
          </Button>
        </>
      )}
    </div>
  );
}
